import { useState } from 'react';
import { FormField, Input } from '@/shared/components';
import { I } from '@/shared/icons';
import { cn } from '@/shared/lib/cn';
import { TriStateButtons } from './TriStateButtons';
import type { AriFormState, AriInitialValues } from './helpers';

interface AdvancedRestrictionsProps {
  form: AriFormState;
  onChange: <K extends keyof AriFormState>(field: K, value: AriFormState[K]) => void;
  current?: AriInitialValues['currentRestrictions'];
}

// Nur die Anzeige: ein vorbelegter Wert ginge mit jeder Änderung über einen breiteren Zeitraum raus.
const currentHint = (value: boolean | null | undefined) =>
  value == null ? '' : `Aktuell: ${value ? 'gesperrt' : 'frei'} · `;

export function AdvancedRestrictions({ form, onChange, current }: AdvancedRestrictionsProps) {
  const [open, setOpen] = useState(false);
  const activeCount =
    (form.maxStay.trim() ? 1 : 0) +
    (form.closedToArrival !== 'unchanged' ? 1 : 0) +
    (form.closedToDeparture !== 'unchanged' ? 1 : 0) +
    (form.stopSell !== 'unchanged' ? 1 : 0);

  return (
    <div className="border-line bg-card-alt/30 mt-3 rounded-md border">
      {/*
        Der Hover-Zustand hing an einem Alias auf `--color-bg-deep`, das keine Tokenquelle kennt.
        Der Auslöser reagierte optisch also gar nicht (dritter Fund dieser Art nach Block D).
      */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="ari-erweiterte-beschraenkungen"
        className="hover:bg-card-alt flex min-h-11 w-full items-center justify-between rounded-md px-3 py-2 transition-colors"
      >
        <span className="text-ink text-sm font-medium">
          Erweiterte Beschränkungen
          {activeCount > 0 && <span className="text-sage ml-2 text-xs">({activeCount} aktiv)</span>}
        </span>
        <I
          name="ChevronDown"
          size={16}
          aria-hidden
          className={cn('transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div
          id="ari-erweiterte-beschraenkungen"
          className="border-line space-y-3 border-t px-3 py-3"
        >
          <FormField
            label="Höchstaufenthalt (Nächte)"
            hint="Leer lassen heißt unverändert · 0 = unbegrenzt · sonst 1 bis 365"
          >
            <Input
              type="number"
              min="0"
              max="365"
              step="1"
              placeholder="z. B. 14"
              value={form.maxStay}
              onChange={(e) => onChange('maxStay', e.target.value)}
            />
          </FormField>
          <FormField
            label="Anreise"
            hint={`${currentHint(current?.closedToArrival)}An diesen Tagen darf kein Aufenthalt beginnen.`}
          >
            <TriStateButtons
              value={form.closedToArrival}
              onChange={(v) => onChange('closedToArrival', v)}
              onLabel="Gesperrt"
              offLabel="Frei"
            />
          </FormField>
          <FormField
            label="Abreise"
            hint={`${currentHint(current?.closedToDeparture)}An diesen Tagen darf kein Aufenthalt enden.`}
          >
            <TriStateButtons
              value={form.closedToDeparture}
              onChange={(v) => onChange('closedToDeparture', v)}
              onLabel="Gesperrt"
              offLabel="Frei"
            />
          </FormField>
          <FormField
            label="Verkaufsstopp"
            hint={'Komplette Sperre für die Buchungsportale — fester als „Blockiert".'}
          >
            <TriStateButtons
              value={form.stopSell}
              onChange={(v) => onChange('stopSell', v)}
              onLabel="Aktiv"
              offLabel="Inaktiv"
            />
          </FormField>
        </div>
      )}
    </div>
  );
}
